import { useContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/core/messageSlice';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Button, Tooltip, Typography } from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import WdPageSimple from '@main/core/WdPageSimple';
import {
  DashboardTable,
  ApexWrapper,
  SimpleHeader,
  CardWidgets,
  BasicSelect,
} from '@main/components';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useComponentRegistry, useRequest, useThemeMediaQuery } from '@main/hooks';
import { getAnalyticsKiosk } from 'src/app/api/kiosk';
import { localDateToPlainText } from 'src/app/utils/dates';

const Root = styled(WdPageSimple)(({ theme }) => ({
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const currentlyDataOptions = [
  {
    label: 'Desactivado',
    value: undefined,
  },
  {
    label: '10seg',
    value: 10_000,
  },
  {
    label: '30seg',
    value: 30_000,
  },
  {
    label: '1min',
    value: 60_000,
  },
  {
    label: '2min',
    value: 120_000,
  },
  {
    label: '10min',
    value: 600_000,
  },
];

const defaultStyles = () => ({ marginInline: '1.4rem', marginBottom: '2rem' });

const Dashboard = () => {
  const [delayTime, setDelayTime] = useState(undefined);
  const [datePickerValue, setDatePickerValue] = useState([null, null]);
  const [dataList, setDataList] = useState([]);
  const intervalRef = useRef(null);

  const dispatch = useDispatch();

  const { finalizeSession } = useContext(BootstrapContext);
  const [form, setForm] = useState({ initDate: '', endDate: '' });

  const isDesktop = useThemeMediaQuery((theme) => theme.breakpoints.up('md'));

  const { registerComponent, ItemWrapper } = useComponentRegistry();

  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };

  const { makeRequest: tryGetData, response } = useRequest(getAnalyticsKiosk, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });

  // ---- Borra todos los filtros ------
  const handleClearAllFilters = () => {
    setDatePickerValue([null, null]);
  };

  const handleSearch = useCallback(() => {
    console.log('FROM | QUERY => ', form);
    tryGetData(form);
  }, [form]);

  useEffect(() => {
    if (datePickerValue === undefined) return;
    setForm((current) => {
      return {
        ...current,
        initDate: localDateToPlainText(datePickerValue[0], { format: 'YYYY-MM-DD' }) || '',
        endDate: localDateToPlainText(datePickerValue[1], { format: 'YYYY-MM-DD' }) || '',
      };
    });
  }, [datePickerValue]);

  useEffect(() => {
    if (typeof delayTime !== 'number') {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }
    intervalRef.current = setInterval(() => {
      handleSearch();
    }, delayTime);
  }, [delayTime]);

  useEffect(() => {
    if (!response) return;
    console.log(response);
    setDataList(response);
  }, [response]);

  // -------- Realiza la peticion de datos al haber un cambio en el formulario -----
  useEffect(() => {
    handleSearch();
  }, [form]);

  // ------ Inicializacion de la vista con toda la informacion disponible -------
  useEffect(() => {
    return () => {
      setDataList([]);
      if (!intervalRef.current) return;
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    registerComponent('table', DashboardTable);
    registerComponent('summary', CardWidgets);
    registerComponent('apex', ApexWrapper);
  }, []);

  useEffect(() => {
    // Primer día del mes en curso
    const firstDate = dayjs().startOf('month');
    // Último día del mes en curso
    const lastDate = dayjs().endOf('month');

    setDatePickerValue([firstDate, lastDate]);
  }, []);

  const MainWrapper = useMemo(() => {
    const normalizeClassName = (str) =>
      str
        .trim()
        .replace(/[^\w\-:[\].%/ ]/g, '')
        .replace(/\s+/g, ' ');

    const handleGetClasses = (item, index) => {
      const currentClassName = item?.data?.className;
      if (!currentClassName) return '';
      return normalizeClassName(currentClassName);
    };

    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (!Array.isArray(dataList) || dataList.length === 0) return <></>;
    return dataList.map((item, itemIndex) => {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      if (!item || !item.success) return <></>;
      const options =
        item && item.data && item.data.component === 'apex'
          ? { options: item?.data?.chartOptions }
          : null;

      return (
        <ItemWrapper
          style={_.merge({}, defaultStyles(), item?.data?.style)}
          key={`component_dash_${itemIndex}`}
          use={item.data.component}
          {..._.omit(item.data, [
            'component',
            'className',
            'style',
            'styles',
            'styledQuery',
            'options',
            'chartOptions',
            'key',
          ])}
          className={handleGetClasses(item, itemIndex)}
          {...options}
        />
      );
    });
  }, [dataList]);

  return (
    <Root
      header={
        <SimpleHeader
          title="Panel de control"
          description="Información general de dispositivos de kiosk"
        >
          <div className="mt-16 md:mt-0 flex w-full items-center md:items-end justify-start md:justify-end flex-col md:flex-row">
            <BasicSelect
              data={currentlyDataOptions}
              onChange={(data) => setDelayTime(data)}
              value={delayTime}
              title="Tiempo de actualizacion"
              size="small"
              color="secondary"
              className="top-4 w-[210px] mx-16"
            />
            <div className="my-16 md:my-0 justify-center" style={{ maxWidth: '40rem' }}>
              <Typography className="text-lg leading-8 text-center truncate">
                Selecciona un rango de fechas
              </Typography>
              <DateRangePicker
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: 'small' } }}
                localeText={{ start: 'Fecha inicial', end: 'Fecha final' }}
                value={datePickerValue}
                onChange={(current) => setDatePickerValue(current)}
              />
            </div>
            <Tooltip title="Limpiar filtros">
              <Button
                className="my-4 sm:my-0 mx-8 sm:mr-0"
                onClick={handleClearAllFilters}
                size="medium"
                color="primary"
                variant="contained"
                startIcon={!isDesktop && <DeleteOutlineIcon />}
                sx={
                  isDesktop
                    ? {
                        minWidth: '0.8rem',
                        padding: '0.72rem',
                      }
                    : {}
                }
              >
                {!isDesktop ? 'Limpiar filtros' : <DeleteOutlineIcon />}
              </Button>
            </Tooltip>
          </div>
        </SimpleHeader>
      }
      content={
        <div className="flex flex-row flex-wrap w-full justify-center md:justify-around">
          {MainWrapper}
        </div>
      }
      scroll="page"
    />
  );
};

export default Dashboard;

